import type {
  ConditionTypesResult,
  ConditionsByTypeResult,
} from 'lib/queries/conditions';

import type { ConditionType } from 'types/condition';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Button, Paragraph, Wrap } from '@leafwell/components';
import Figure from './figure';

import { useWordPressContext } from 'contexts/wordpress-provider';

import ConditionsList from './conditions-list';
import Pagination from './pagination';
import { SelectField } from './select-field';
import { useEventsTracker } from 'contexts/eventsTracker';

type TypesListProps = {
  onChange: (item: ConditionType) => void;
  activeItem: string;
  items: ConditionTypesResult;
};

const TypesListGrid: React.FC<TypesListProps> = ({
  activeItem,
  items,
  onChange,
}) => {
  const { asPath, locale } = useRouter();
  const pathname =
    locale === 'en'
      ? asPath.split('?')[0]
      : `/${locale}${asPath.split('?')[0]}`;

  return (
    <ul className="flex flex-wrap gap-x-2 gap-y-2.5 justify-start col-span-full">
      {items?.conditionTypes?.map(item => {
        const isActive = activeItem === item.slug;
        return (
          <li
            {...{
              key: item.slug,
            }}
          >
            <Button
              label={item.name}
              size="small"
              variant="secondary"
              filled={isActive}
              rounded
              className={[
                'font-sans whitespace-nowrap',
                isActive
                  ? 'pointer-events-none font-medium'
                  : '!border-indigo-200',
              ].join(' ')}
              onClick={() => {
                onChange(item);
              }}
              href={`${pathname}?condition_type=${item?.slug}`}
            />
          </li>
        );
      })}
    </ul>
  );
};

const TypesListSelect: React.FC<TypesListProps> = ({
  activeItem,
  items,
  onChange,
}) => {
  const { asPath, locale } = useRouter();
  const pathname =
    locale === 'en'
      ? asPath.split('?')[0]
      : `/${locale}${asPath.split('?')[0]}`;

  const conditionTypesArray = items?.conditionTypes?.map(item => {
    return { label: item.name, value: item.slug };
  });

  const handleChange = (slug: string) => {
    const condition = items?.conditionTypes?.find(item => item.slug == slug);
    onChange(condition);

    if (typeof window !== undefined) {
      window.location.href = `${pathname}?condition_type=${slug}`;
    }
  };

  return (
    <SelectField
      className="w-56 col-span-ful"
      id="conditiontypes"
      options={conditionTypesArray}
      placeholder="Condition Types"
      name="condition"
      value={activeItem}
      onBlur={() => ''}
      onChange={condition => handleChange(condition)}
    />
  );
};

const TypesList: React.FC<TypesListProps & { selectConditions: boolean }> = ({
  activeItem,
  items,
  onChange,
  selectConditions,
}) => {
  if (selectConditions) {
    return (
      <TypesListSelect
        {...{
          activeItem,
          items,
          onChange,
        }}
      />
    );
  }
  return (
    <TypesListGrid
      {...{
        activeItem,
        items,
        onChange,
      }}
    />
  );
};

const ConditionsByType: React.FC<
  ConditionsByTypeResult & {
    conditionTypes?: ConditionTypesResult;
    className?: string;
    activeCondition?: string;
  }
> = ({
  conditions,
  pagination,
  className = '',
  conditionTypes,
  conditionType,
  activeCondition,
}) => {
  const { conditionTypesImage, translations } = useWordPressContext();

  const [activeConditionType, updateActiveConditionType] =
    useState(conditionType);

  const [selectConditions, toogleSelect] = useState(false);
  const toggleWidth = 768;

  useEffect(() => {
    function windowResize() {
      const windowWidth = window.innerWidth;
      toogleSelect(windowWidth <= toggleWidth);
    }

    const resizeTimer = setTimeout(() => windowResize(), 200);

    window.addEventListener('resize', windowResize);

    () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', windowResize);
    };
  }, []);

  const { trackEvent } = useEventsTracker();

  return conditionTypes ? (
    <Wrap
      className={[
        'px-reset content-start lg:grid-cols-12 !mb-0 !pb-0',
        className,
      ].join(' ')}
    >
      <Paragraph
        size="small"
        className="font-medium self-end -mb-8 lg:col-span-full"
      >
        {translations['Select a body area']}
      </Paragraph>
      <TypesList
        activeItem={activeConditionType}
        items={conditionTypes}
        onChange={conditionType => {
          trackEvent({
            event: 'select_content',
            content_type: conditionType.name,
            item_id: 'condition_type_filter',
            origin: 'medical_conditions',
          });
          updateActiveConditionType(conditionType?.slug);
        }}
        selectConditions={selectConditions}
      />
      {conditionTypesImage && (
        <span className="hidden absolute -z-1 lg:block lg:top-0 lg:right-0">
          <Figure {...conditionTypesImage} />
        </span>
      )}
      <ConditionsList
        conditions={conditions}
        activeCondition={activeCondition}
        className="col-span-full"
        title={
          conditionTypes?.conditionTypes.find(
            ({ slug }) => slug === activeConditionType,
          )?.name
        }
      />
      {conditions?.length ? (
        <Pagination
          limit={pagination.limit}
          offset={pagination.offset}
          total={pagination.total}
          className={'col-span-full'}
        />
      ) : null}
    </Wrap>
  ) : null;
};

export default ConditionsByType;
